import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatXOF } from "@/lib/format";
import {
  Buildings, Users, ChalkboardTeacher, Books, CurrencyDollar, TrendUp, TrendDown,
  Warning, ChartPieSlice, Bell,
} from "@phosphor-icons/react";

const STAGE_LABELS = {
  preschool: "Maternelle",
  primary: "Primaire",
  lower_secondary: "Collège",
  upper_secondary: "Lycée",
  other: "Autre",
};

const STAGE_COLORS = {
  preschool: "bg-secondary/70",
  primary: "bg-primary",
  lower_secondary: "bg-accent",
  upper_secondary: "bg-primary/60",
  other: "bg-muted-foreground",
};

export default function DirectorDashboard() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/director/dashboard");
        setD(r.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-10 text-sm text-muted-foreground">Chargement du command center…</div>;
  if (!d) return null;

  const { counts, students_by_stage, attendance_today, finance_month, top_unpaid_families, pending_grading_courses, recent_notifications, active_period } = d;
  const totalByStage = Object.values(students_by_stage).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Command Center</div>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
          <Buildings size={32} weight="duotone" className="text-primary" />
          Direction — vue d'ensemble
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {active_period ? `${active_period.name} en cours · ` : ""}Données temps réel de l'établissement
        </p>
      </header>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi icon={Users} label="Élèves actifs" value={counts.students} tint="primary" testid="kpi-students" />
        <Kpi icon={ChalkboardTeacher} label="Enseignants" value={counts.teachers} tint="secondary" testid="kpi-teachers" />
        <Kpi icon={Buildings} label="Classes" value={counts.classes} tint="accent" testid="kpi-classes" />
        <Kpi icon={Books} label="Cours actifs" value={counts.active_courses} tint="primary" testid="kpi-courses" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Students by stage */}
        <Card className="p-5 lg:col-span-1">
          <div className="flex items-center gap-2 mb-3">
            <ChartPieSlice size={18} weight="duotone" className="text-primary" />
            <div className="font-heading font-semibold">Répartition</div>
          </div>
          <div className="space-y-2">
            {Object.entries(students_by_stage).map(([k, v]) => {
              const pct = totalByStage ? Math.round((v / totalByStage) * 100) : 0;
              return (
                <div key={k} data-testid={`stage-${k}`}>
                  <div className="flex justify-between text-xs">
                    <span>{STAGE_LABELS[k] || k}</span>
                    <span className="tabular-nums text-muted-foreground">{v} · {pct}%</span>
                  </div>
                  <div className="mt-0.5 h-2 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full ${STAGE_COLORS[k] || "bg-muted-foreground"}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Attendance today */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <TrendUp size={18} weight="duotone" className="text-secondary" />
            <div className="font-heading font-semibold">Présence aujourd'hui</div>
          </div>
          {attendance_today.total === 0 ? (
            <div className="text-sm text-muted-foreground">Aucun appel effectué aujourd'hui.</div>
          ) : (
            <>
              <div className="font-heading text-4xl font-bold text-secondary tabular-nums">
                {attendance_today.rate ?? "—"}<span className="text-lg text-muted-foreground font-normal">%</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">présents sur {attendance_today.total} appels</div>
              <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                <SmallStat label="Présent" v={attendance_today.summary.present} color="text-secondary" />
                <SmallStat label="Absent" v={attendance_today.summary.absent} color="text-accent" />
                <SmallStat label="Retard" v={attendance_today.summary.late} color="text-primary" />
                <SmallStat label="Excusé" v={attendance_today.summary.excused} color="text-muted-foreground" />
              </div>
            </>
          )}
        </Card>

        {/* Finance month */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <CurrencyDollar size={18} weight="duotone" className="text-accent" />
            <div className="font-heading font-semibold">Trésorerie du mois</div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{finance_month.month}</div>
          <div className="mt-2 space-y-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-secondary">Encaissements</div>
              <div className="font-heading text-xl font-bold text-secondary tabular-nums" data-testid="finance-revenue">{formatXOF(finance_month.revenue)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-accent">Dépenses</div>
              <div className="font-heading text-xl font-bold text-accent tabular-nums" data-testid="finance-expenses">{formatXOF(finance_month.expenses)}</div>
            </div>
            <div className="pt-2 border-t border-border">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Solde</div>
              <div className={`font-heading text-2xl font-bold tabular-nums ${finance_month.profit >= 0 ? "text-secondary" : "text-accent"}`} data-testid="finance-profit">
                {finance_month.profit >= 0 ? "+" : ""}{formatXOF(finance_month.profit)}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Alerts & top unpaid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Warning size={18} weight="fill" className="text-accent" />
            <div className="font-heading font-semibold">Familles avec impayés</div>
            <Badge variant="outline" className="ml-auto">{top_unpaid_families.length}</Badge>
          </div>
          {top_unpaid_families.length === 0 ? (
            <div className="text-sm text-muted-foreground">Aucun impayé — bravo !</div>
          ) : (
            <div className="space-y-2">
              {top_unpaid_families.map((f, i) => (
                <div key={f.student_id} className="flex items-center justify-between text-sm border-b border-border last:border-0 py-2" data-testid={`unpaid-${i}`}>
                  <div>
                    <div className="font-medium">{f.student.last_name} {f.student.first_name}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{f.student.matricule}</div>
                  </div>
                  <div className="font-mono font-bold text-accent tabular-nums">{formatXOF(f.amount)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <TrendDown size={18} weight="duotone" className="text-accent" />
            <div className="font-heading font-semibold">Cours en retard de correction</div>
          </div>
          <div className="font-heading text-4xl font-bold tabular-nums" data-testid="pending-grading">{pending_grading_courses}</div>
          <div className="text-xs text-muted-foreground mt-1">
            cours dont les notes ne sont pas complètes pour {active_period?.name || "la période active"}
          </div>
        </Card>
      </div>

      {/* Recent notifications */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Bell size={18} weight="duotone" className="text-primary" />
          <div className="font-heading font-semibold">Notifications récentes</div>
        </div>
        {recent_notifications.length === 0 ? (
          <div className="text-sm text-muted-foreground">Aucune notification récente.</div>
        ) : (
          <div className="space-y-1.5">
            {recent_notifications.slice(0, 10).map(n => (
              <div key={n.id} className="text-xs border-l-2 border-primary/40 pl-3 py-1" data-testid={`notif-${n.id}`}>
                <div className="font-medium">{n.subject || "—"}</div>
                <div className="text-muted-foreground">{n.message}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{n.created_at ? new Date(n.created_at).toLocaleString("fr-FR") : ""}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tint, testid }) {
  const styles = {
    primary: "bg-primary/10 text-primary",
    secondary: "bg-secondary/10 text-secondary",
    accent: "bg-accent/10 text-accent",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className={`w-10 h-10 rounded-md ${styles[tint] || styles.primary} flex items-center justify-center mb-3`}>
        <Icon size={20} weight="duotone" />
      </div>
      <div className="font-heading text-3xl font-bold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wide">{label}</div>
    </Card>
  );
}
function SmallStat({ label, v, color }) {
  return (
    <div>
      <div className={`font-heading text-lg font-bold tabular-nums ${color}`}>{v}</div>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}
